import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from "react-redux";
import {
    TrendingUp,
    Target,
    Settings as SettingsIcon,
    Shield,
    Rocket,
    Save,
    ArrowLeft,
    CheckCircle2,
    Tag,
    RefreshCw,
    Edit3,
    Check,
    X,
    ShoppingCart,
    Percent,
    DollarSign,
    Package,
    MousePointer,
    Move,
    Eye,
    Code
} from 'lucide-react';
import styles from './styles.module.css';
import { launchABTest } from '../../../../../services/mongod.data';
import { SelectUserWebsiteURL } from '../../../../DesignEditor/designEditorSlice';
import { selectLogInUsername } from '../../../../../common/authenticateDashboard/authSlice';

const TestSummary = ({ testType, formData, onBack, onUpdate }) => {
    const storeUrl = useSelector(SelectUserWebsiteURL)
    const sessionUser = useSelector(selectLogInUsername);
    const cf_username = sessionUser?.split('-prod@')[0]
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [toast, setToast] = useState(null);

    // Coupon code states - only for pricing/cart tests
    const [couponCode, setCouponCode] = useState('');
    const [isEditingCoupon, setIsEditingCoupon] = useState(false);
    const [tempCouponCode, setTempCouponCode] = useState('');
    const [showCouponAlert, setShowCouponAlert] = useState(false);

    const settings = formData?.settings || {};
    const basicSettings = settings?.basic || {};
    const advancedSettings = settings?.advanced || {};
    const managementSettings = settings?.management || {};
    const qualitySettings = settings?.quality || {};

    // Calculate expected values
    const duration = basicSettings.duration || 14;
    const sampleSize = basicSettings.sampleSize || 1000;
    const confidence = basicSettings.confidence || 95;
    const trafficSplit = basicSettings.trafficSplit || 50;

    // Check test types
    const isCartTest = testType === 'cart' || formData.variants?.testType === 'cart';
    const isPricingTest = testType === 'pricing' || formData.variants?.testType === 'pricing';
    const isElementTest = testType === 'element' || formData.variants?.testType === 'element';
    // const isElementTest = testType === 'element' || formData.elementConfig;

    // Generate random coupon code - only for pricing/cart tests
    const generateRandomCouponCode = () => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        let result = '';
        for (let i = 0; i < 12; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return result;
    };

    // Format money utility
    const formatMoney = (amount, currency = 'USD') => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency,
        }).format(amount);
    };

    // Initialize coupon code on component mount - only for pricing/cart tests
    useEffect(() => {
        if (isElementTest) {
            return; // Skip coupon code for element tests
        }

        if (formData?.discountInfo?.couponCode) {
            setCouponCode(formData.discountInfo.couponCode);
        } else {
            const newCode = generateRandomCouponCode();
            setCouponCode(newCode);
            onUpdate({
                discountInfo: {
                    couponCode: newCode,
                    use: true
                }
            });
        }
    }, [isElementTest]);

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3000);
    };

    const handleRegenerateCoupon = () => {
        if (isElementTest) return;

        const newCode = generateRandomCouponCode();
        setCouponCode(newCode);
        onUpdate({
            discountInfo: {
                couponCode: newCode,
                use: true
            }
        });
        showToast('✅ New coupon code generated!', 'success');
    };

    const handleEditCoupon = () => {
        setTempCouponCode(couponCode);
        setIsEditingCoupon(true);
    };

    const handleSaveCoupon = () => {
        if (!tempCouponCode.trim()) {
            showToast('❌ Coupon code cannot be empty', 'error');
            return;
        }

        if (!/^[A-Z0-9]+$/.test(tempCouponCode.trim())) {
            showToast('❌ Coupon code must contain only letters and numbers', 'error');
            return;
        }

        const updatedCode = tempCouponCode.trim().toUpperCase();
        setCouponCode(updatedCode);
        setIsEditingCoupon(false);

        onUpdate({
            discountInfo: {
                couponCode: updatedCode,
                use: true
            }
        });

        showToast('✅ Coupon code updated!', 'success');
    };

    const handleCancelEditCoupon = () => {
        setTempCouponCode('');
        setIsEditingCoupon(false);
    };

    const prepareTestData = (status) => {
        const now = new Date().toISOString();

        const baseData = {
            ...formData,
            metadata: {
                createdAt: formData.metadata?.createdAt || now,
                updatedAt: now,
                status: status,
                testId: formData.metadata?.testId || `test_${Date.now()}`,
                version: (formData.metadata?.version || 0) + 1
            }
        };

        // Only add discount info for pricing/cart tests
        if (!isElementTest) {
            baseData.discountInfo = {
                couponCode: couponCode,
                use: true
            };
        }

        return baseData;
    };

    // Create Shopify discount code API call - only for pricing/cart tests
    // const createShopifyDiscount = async (discountCode, discountPercentage, productId, cartConfig = null) => {
    //     if (isElementTest) return { success: true }; // Skip for element tests

    //     try {
    //         const payload = {
    //             code: discountCode,
    //             shopDomain: cf_username,
    //             duration: duration
    //         };

    //         if (isCartTest && cartConfig) {
    //             payload.testType = 'cart';
    //             if (cartConfig.discountType === 'percentage') {
    //                 payload.percentage = cartConfig.discountValue;
    //             } else if (cartConfig.discountType === 'fixed_amount') {
    //                 payload.fixedAmount = cartConfig.discountValue;
    //             }
    //             if (cartConfig.triggerType === 'cart_value') {
    //                 payload.minimumAmount = cartConfig.threshold;
    //             } else if (cartConfig.triggerType === 'cart_quantity') {
    //                 payload.minimumQuantity = cartConfig.threshold;
    //             }
    //         } else {
    //             payload.testType = 'pricing';
    //             payload.percentage = discountPercentage;
    //             if (productId) {
    //                 payload.productId = productId;
    //             }
    //         }

    //         const response = await fetch('/api/shopify/create-discount-v2', {
    //             method: 'POST',
    //             headers: {
    //                 'Content-Type': 'application/json',
    //                 'X-Shop-Username': cf_username
    //             },
    //             body: JSON.stringify(payload)
    //         });

    //         if (!response.ok) {
    //             const errorData = await response.json();
    //             throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    //         }

    //         const result = await response.json();
    //         return result;
    //     } catch (error) {
    //         console.error('Error creating Shopify discount:', error);
    //         throw error;
    //     }
    // };

    const handleSaveDraft = async () => {
        setIsSubmitting(true);

        const testData = prepareTestData('draft');
        console.log('💾 Saving Draft:', testData);

        try {
            const response = await fetch(`/api/abtest/save`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Shop-Username': cf_username
                },
                body: JSON.stringify({
                    ...testData,
                    shopDomain: cf_username,
                    storeUrl: storeUrl
                })
            });

            const result = await response.json();

            if (response.ok && result.success) {
                showToast('✅ Test saved as draft successfully!', 'success');
                onUpdate({ metadata: testData.metadata });

                setTimeout(() => {
                    navigate('/abtest');
                }, 1500);
            } else {
                throw new Error(result.error || 'Failed to save draft');
            }
        } catch (error) {
            console.error('Error saving draft:', error);
            showToast('❌ Failed to save draft. Please try again.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleLaunchTest = async () => {
        setIsSubmitting(true);

        // Only show coupon alert for pricing/cart tests
        if (!isElementTest) {
            setShowCouponAlert(true);
        }

        const testData = prepareTestData('active');
        console.log('🚀 Launching Test:', testData);

        try {
            let discountPercentage = 0;
            let productId = '';

            // Only handle discount for pricing/cart tests
            // if (!isElementTest) {
            //     if (isPricingTest) {
            //         const variantPrices = formData.variants?.variantPrices || {};
            //         const firstVariant = Object.values(variantPrices)[0];
            //         discountPercentage = firstVariant?.discount || 0;
            //         productId = formData.variants?.productId || '';
            //     } else if (isCartTest) {
            //         const cartConfig = formData.variants;
            //         if (cartConfig?.discountType === 'percentage') {
            //             discountPercentage = cartConfig?.discountValue || 0;
            //         }
            //     }

            //     // Create Shopify discount code
            //     const shouldCreateDiscount = isPricingTest
            //         ? discountPercentage > 0
            //         : isCartTest && formData.variants?.discountValue > 0;

            //     if (shouldCreateDiscount) {
            //         showToast('🔄 Creating discount code in Shopify...', 'info');

            //         try {
            //             let discountResult;
            //             if (isCartTest) {
            //                 discountResult = await createShopifyDiscount(
            //                     couponCode,
            //                     0,
            //                     '',
            //                     formData.variants
            //                 );
            //             } else {
            //                 discountResult = await createShopifyDiscount(
            //                     couponCode,
            //                     discountPercentage,
            //                     productId
            //                 );
            //             }

            //             if (discountResult.success) {
            //                 showToast(`✅ Discount code "${couponCode}" created successfully!`, 'success');
            //             } else {
            //                 throw new Error(discountResult.error || 'Failed to create discount');
            //             }
            //         } catch (discountError) {
            //             console.error('Discount creation failed:', discountError);
            //             setShowCouponAlert(false);
            //             showToast(
            //                 `⚠️ Failed to create discount code: ${discountError.message}`,
            //                 'warning'
            //             );

            //             const shouldContinue = window.confirm(
            //                 'Failed to create discount code in Shopify. Do you want to continue launching the test anyway? You can create the discount code manually.'
            //             );

            //             if (!shouldContinue) {
            //                 setIsSubmitting(false);
            //                 return;
            //             }
            //         }
            //     }
            // }

            console.log('🚀 Sending launch request to backend with data:', testData);
            const result  = await launchABTest(testData,cf_username);

            console.log('🚀 Launch result from backend:', result);

            if (result.success) {
                setShowCouponAlert(false);
                showToast('🎉 Test launched successfully!', 'success');
                onUpdate({ metadata: testData.metadata });

                setTimeout(() => {
                    if (isElementTest) {
                        const elementConfig = formData.variants?.modifications;
                        alert(
                            `✅ Element Test Launched Successfully!\n\n` +
                            `Button: ${elementConfig?.buttonName || 'Selected Button'}\n` +
                            `Selector: ${elementConfig?.selector || 'N/A'}\n` +
                            `Target: ${elementConfig?.position?.test?.targetName || 'N/A'}\n` +
                            `Position: ${elementConfig?.position?.test?.position || 'N/A'}\n\n` +
                            `Your element position test is now live and tracking visitor behavior.`
                        );
                    } else if (isCartTest) {
                        const cartConfig = formData.variants;
                        const discountDisplay = cartConfig?.discountType === 'percentage'
                            ? `${cartConfig.discountValue}%`
                            : formatMoney(cartConfig?.discountValue || 0);

                        alert(
                            `✅ Cart Discount Test Launched Successfully!\n\n` +
                            `Discount Code: ${couponCode}\n` +
                            `Discount: ${discountDisplay} OFF\n` +
                            `Trigger: ${cartConfig?.triggerType === 'cart_value' ? 'Cart Value' : 'Cart Quantity'}\n` +
                            `Threshold: ${cartConfig?.triggerType === 'cart_value'
                                ? formatMoney(cartConfig?.threshold || 0)
                                : `${cartConfig?.threshold || 0} items`}\n\n` +
                            `Your cart discount test is now live and tracking visitor behavior.`
                        );
                    } else if (isPricingTest) {
                        alert(
                            `✅ Test Launched Successfully!\n\n` +
                            `Discount Code: ${couponCode}\n` +
                            `Discount: ${discountPercentage}% OFF\n` +
                            `Product: ${formData.variants?.productTitle}\n\n` +
                            `The discount code has been created in your Shopify admin and is now active.`
                        );
                    }

                    setTimeout(() => {
                        navigate('/goals');
                    }, 500);
                }, 1000);
            } else {
                throw new Error(result.error || 'Failed to launch test');
            }
        } catch (error) {
            console.error('Error launching test:', error);
            setShowCouponAlert(false);
            showToast('❌ Failed to launch test. Please try again.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const getMetricLabel = (metric) => {
        const labels = {
            conversion: 'Conversion Rate',
            ctr: 'Click-through Rate',
            revenue: 'Revenue per Visitor',
            engagement: 'Engagement Time'
        };
        return labels[metric] || 'Conversion Rate';
    };

    const getDeviceLabel = (device) => {
        const labels = {
            mobile: 'Mobile Only',
            desktop: 'Desktop Only',
            tablet: 'Tablet Only',
            all: 'All Devices'
        };
        return labels[device] || 'All Devices';
    };

    const getUserTypeLabel = (userType) => {
        const labels = {
            'first-time': 'First-time',
            returning: 'Returning',
            'logged-in': 'Logged-in',
            anonymous: 'Anonymous',
            all: 'All Users'
        };
        return labels[userType] || 'All Users';
    };

    const getTrafficSourceLabel = (source) => {
        const labels = {
            organic: 'Organic Only',
            paid: 'Paid Only',
            direct: 'Direct',
            social: 'Social Media',
            email: 'Email',
            all: 'All Traffic'
        };
        return labels[source] || 'All Traffic';
    };

    const getCountryLabel = (country) => {
        const labels = {
            us: 'United States',
            uk: 'United Kingdom',
            ca: 'Canada',
            au: 'Australia',
            in: 'India',
            all: 'All Countries'
        };
        return labels[country] || 'All Countries';
    };

    const getAutoStopLabel = (autoStop) => {
        const labels = {
            manual: 'Manual stop only',
            significant: 'When significant',
            duration: 'After duration',
            sample: 'Sample reached'
        };
        return labels[autoStop] || 'When significant';
    };

    const getNotificationsLabel = (notifications) => {
        const labels = {
            none: 'No notifications',
            daily: 'Daily updates',
            weekly: 'Weekly summary',
            milestones: 'Key milestones'
        };
        return labels[notifications] || 'Key milestones';
    };

    // Get discount percentage for display - only for pricing tests
    const getDiscountPercentage = () => {
        if (isElementTest) return 0;

        if (isPricingTest) {
            const variantPrices = formData.variants?.variantPrices || {};
            const firstVariant = Object.values(variantPrices)[0];
            return firstVariant?.discount || 0;
        } else if (isCartTest) {
            const cartConfig = formData.variants;
            return cartConfig?.discountType === 'percentage' ? cartConfig?.discountValue || 0 : 0;
        }
        return 0;
    };

    // Render Element Test Details - NEW
    const renderElementTestDetails = () => {
        const elementConfig = formData.variants?.modifications || {};
        const selectedButton = formData.variants?.selectedButton || {};
        const positionConfig = formData.variants?.modifications?.position || {};
        // const positionConfig = elementConfig.position || {};

        return (
            <div className={styles.variantComparison}>
                {/* Control Group */}
                <div className={styles.variantCard}>
                    <div className={styles.variantHeader}>
                        <div className={styles.variantBadge} style={{ background: '#dbeafe', color: '#1d4ed8' }}>
                            Control Group
                        </div>
                        <div className={styles.variantPercentage}>{trafficSplit}%</div>
                    </div>
                    <div className={styles.variantContent}>
                        <div className={styles.elementInfo}>
                            <div className={styles.elementIcon}>
                                <MousePointer size={48} color="#6b7280" />
                            </div>
                            <div className={styles.elementDetails}>
                                <h4>Original Button Position</h4>
                                <p>Current button location and behavior</p>
                                <div className={styles.elementSpecs}>
                                    <div className={styles.specItem}>
                                        <span className={styles.specLabel}>Button:</span>
                                        <span className={styles.specValue}>{elementConfig.buttonName || 'Button'}</span>
                                    </div>
                                    <div className={styles.specItem}>
                                        <span className={styles.specLabel}>Selector:</span>
                                        <code className={styles.selectorCode}>{elementConfig.selector || 'N/A'}</code>
                                    </div>
                                    <div className={styles.specItem}>
                                        <span className={styles.specLabel}>Position:</span>
                                        <span className={styles.specValue}>Current Location</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <button className={styles.previewButton}>
                            <Eye size={16} />
                            Preview Original
                        </button>
                    </div>
                </div>

                {/* Variant Group */}
                <div className={styles.variantCard}>
                    <div className={styles.variantHeader}>
                        <div className={styles.variantBadge} style={{ background: '#dcfce7', color: '#166534' }}>
                            Variant Group
                        </div>
                        <div className={styles.variantPercentage}>{100 - trafficSplit}%</div>
                    </div>
                    <div className={styles.variantContent}>
                        <div className={styles.elementInfo}>
                            <div className={styles.elementIcon}>
                                <Move size={48} color="#10b981" />
                            </div>
                            <div className={styles.elementDetails}>
                                <h4>New Button Position</h4>
                                <p>Optimized button placement for better conversions</p>
                                <div className={styles.elementSpecs}>
                                    <div className={styles.specItem}>
                                        <span className={styles.specLabel}>Button:</span>
                                        <span className={styles.specValue}>{elementConfig.buttonName || 'Button'}</span>
                                    </div>
                                    <div className={styles.specItem}>
                                        <span className={styles.specLabel}>Target:</span>
                                        <span className={styles.specValue}>{positionConfig.test?.targetName || 'N/A'}</span>
                                    </div>
                                    <div className={styles.specItem}>
                                        <span className={styles.specLabel}>Method:</span>
                                        <span className={styles.specValue}>
                                            {positionConfig.test?.position === 'append' ? 'Insert as Last Child' :
                                                positionConfig.test?.position === 'prepend' ? 'Insert as First Child' :
                                                    positionConfig.test?.position === 'before' ? 'Insert Before' :
                                                        positionConfig.test?.position === 'after' ? 'Insert After' : 'Move'}
                                        </span>
                                    </div>
                                    {positionConfig.test?.targetSelector && (
                                        <div className={styles.specItem}>
                                            <span className={styles.specLabel}>Selector:</span>
                                            <code className={styles.selectorCode}>{positionConfig.test.targetSelector}</code>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <button className={styles.previewButton}>
                            <Eye size={16} />
                            Preview Variant
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // Render Cart Discount Test Details (existing)
    const renderCartTestDetails = () => {
        const cartConfig = formData.variants || {};

        return (
            <div className={styles.variantComparison}>
                {/* Control Group */}
                <div className={styles.variantCard}>
                    <div className={styles.variantHeader}>
                        <div className={styles.variantBadge} style={{ background: '#dbeafe', color: '#1d4ed8' }}>
                            Control Group
                        </div>
                        <div className={styles.variantPercentage}>{trafficSplit}%</div>
                    </div>
                    <div className={styles.variantContent}>
                        <div className={styles.cartDiscountInfo}>
                            <div className={styles.cartIcon}>
                                <ShoppingCart size={48} color="#6b7280" />
                            </div>
                            <div className={styles.cartDetails}>
                                <h4>Standard Cart Experience</h4>
                                <p>No special discount offers</p>
                                <div className={styles.cartFeatures}>
                                    <span>• Regular pricing</span>
                                    <span>• Standard checkout flow</span>
                                    <span>• No dynamic discounts</span>
                                </div>
                            </div>
                        </div>
                        <button className={styles.previewButton}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                <polyline points="15 3 21 3 21 9" />
                                <line x1="10" y1="14" x2="21" y2="3" />
                            </svg>
                            Preview Control
                        </button>
                    </div>
                </div>

                {/* Variant Group */}
                <div className={styles.variantCard}>
                    <div className={styles.variantHeader}>
                        <div className={styles.variantBadge} style={{ background: '#dcfce7', color: '#166534' }}>
                            Variant Group
                        </div>
                        <div className={styles.variantPercentage}>{100 - trafficSplit}%</div>
                    </div>
                    <div className={styles.variantContent}>
                        <div className={styles.cartDiscountInfo}>
                            <div className={styles.cartIcon}>
                                {cartConfig.discountType === 'percentage' ? (
                                    <Percent size={48} color="#10b981" />
                                ) : (
                                    <DollarSign size={48} color="#10b981" />
                                )}
                            </div>
                            <div className={styles.cartDetails}>
                                <h4>Dynamic Cart Discount</h4>
                                <p>Smart discount offers based on cart behavior</p>
                                <div className={styles.discountSpec}>
                                    <div className={styles.specItem}>
                                        <span className={styles.specLabel}>Trigger:</span>
                                        <span className={styles.specValue}>
                                            {cartConfig.triggerType === 'cart_value' ? (
                                                <>Cart value ≥ {formatMoney(cartConfig.threshold || 0)}</>
                                            ) : (
                                                <>Quantity ≥ {cartConfig.threshold || 0} items</>
                                            )}
                                        </span>
                                    </div>
                                    <div className={styles.specItem}>
                                        <span className={styles.specLabel}>Discount:</span>
                                        <span className={styles.specValue}>
                                            {cartConfig.discountType === 'percentage' ? (
                                                <>{cartConfig.discountValue}% OFF</>
                                            ) : (
                                                <>{formatMoney(cartConfig.discountValue || 0)} OFF</>
                                            )}
                                        </span>
                                    </div>
                                    <div className={styles.specItem}>
                                        <span className={styles.specLabel}>Display:</span>
                                        <span className={styles.specValue}>
                                            {cartConfig.displayPosition === 'cart_drawer' ? 'Cart Drawer' :
                                                cartConfig.displayPosition === 'cart_page' ? 'Cart Page' : 'Site Banner'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <button className={styles.previewButton}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                <polyline points="15 3 21 3 21 9" />
                                <line x1="10" y1="14" x2="21" y2="3" />
                            </svg>
                            Preview Variant
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // Render Pricing Test Details (existing logic)
    const renderPricingTestDetails = () => {
        const variantPrices = formData.variants?.variantPrices || {};
        const variantCount = Object.keys(variantPrices).length;
        const isSingleVariant = variantCount === 1;
        const firstVariant = Object.values(variantPrices)[0];

        return (
            <div className={styles.variantComparison}>
                {/* Control Group */}
                <div className={styles.variantCard}>
                    <div className={styles.variantHeader}>
                        <div className={styles.variantBadge} style={{ background: '#dbeafe', color: '#1d4ed8' }}>
                            Control Group
                        </div>
                        <div className={styles.variantPercentage}>{trafficSplit}%</div>
                    </div>
                    <div className={styles.variantContent}>
                        <div className={styles.productInfo}>
                            <div className={styles.productImage}>
                                {formData.variants?.productImage ? (
                                    <img src={formData.variants.productImage} alt={formData.variants.productTitle} />
                                ) : (
                                    <div className={styles.imagePlaceholder}>No Image</div>
                                )}
                            </div>
                            <div className={styles.productDetails}>
                                <h4>{formData.variants?.productTitle || 'Product'}</h4>
                                <p>ID: {formData.variants?.productId?.split('/').pop() || 'N/A'}</p>

                                {isSingleVariant && firstVariant ? (
                                    <div className={styles.priceDisplay}>
                                        <span className={styles.priceValue}>${firstVariant.originalPrice}</span>
                                    </div>
                                ) : (
                                    <div className={styles.priceList}>
                                        {Object.values(variantPrices).map((vPrice, idx) => (
                                            <div key={idx} className={styles.priceItem}>
                                                <span className={styles.variantName}>{vPrice.variantTitle}</span>
                                                <span className={styles.priceValue}>${vPrice.originalPrice}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        <button className={styles.previewButton}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1-2-2h6" />
                                <polyline points="15 3 21 3 21 9" />
                                <line x1="10" y1="14" x2="21" y2="3" />
                            </svg>
                            Preview Control
                        </button>
                    </div>
                </div>

                {/* Variant Group */}
                <div className={styles.variantCard}>
                    <div className={styles.variantHeader}>
                        <div className={styles.variantBadge} style={{ background: '#dcfce7', color: '#166534' }}>
                            Variant Group
                        </div>
                        <div className={styles.variantPercentage}>{100 - trafficSplit}%</div>
                    </div>
                    <div className={styles.variantContent}>
                        <div className={styles.productInfo}>
                            <div className={styles.productImage}>
                                {formData.variants?.productImage ? (
                                    <img src={formData.variants.productImage} alt={formData.variants.productTitle} />
                                ) : (
                                    <div className={styles.imagePlaceholder}>No Image</div>
                                )}
                            </div>
                            <div className={styles.productDetails}>
                                <h4>{formData.variants?.productTitle || 'Product'}</h4>
                                <p>ID: {formData.variants?.productId?.split('/').pop() || 'N/A'}</p>

                                {isSingleVariant && firstVariant ? (
                                    <div className={styles.priceGroup}>
                                        <span className={styles.oldPrice}>${firstVariant.originalPrice}</span>
                                        <span className={styles.newPrice}>${firstVariant.testPrice}</span>
                                        {firstVariant.discount > 0 && (
                                            <span className={styles.discount}>{firstVariant.discount}% OFF</span>
                                        )}
                                    </div>
                                ) : (
                                    <div className={styles.priceList}>
                                        {Object.values(variantPrices).map((vPrice, idx) => (
                                            <div key={idx} className={styles.priceItem}>
                                                <span className={styles.variantName}>{vPrice.variantTitle}</span>
                                                <div className={styles.priceGroup}>
                                                    <span className={styles.oldPrice}>${vPrice.originalPrice}</span>
                                                    <span className={styles.newPrice}>${vPrice.testPrice}</span>
                                                    {vPrice.discount > 0 && (
                                                        <span className={styles.discount}>{vPrice.discount}% OFF</span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        <button className={styles.previewButton}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                <polyline points="15 3 21 3 21 9" />
                                <line x1="10" y1="14" x2="21" y2="3" />
                            </svg>
                            Preview Variant
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={styles.container}>
            {/* Toast Notification */}
            {toast && (
                <div className={`${styles.toast} ${styles[toast.type]}`}>
                    {toast.message}
                </div>
            )}

            {/* Coupon Alert Modal - Only for pricing/cart tests */}
            {showCouponAlert && !isElementTest && (
                <div className={styles.couponAlertOverlay}>
                    <div className={styles.couponAlertModal}>
                        <div className={styles.couponAlertIcon}>
                            <RefreshCw size={24} className={styles.spinIcon} />
                        </div>
                        <h3 className={styles.couponAlertTitle}>Creating Discount Code</h3>
                        <p className={styles.couponAlertText}>
                            Setting up "{couponCode}" in your Shopify store...
                        </p>
                    </div>
                </div>
            )}

            {/* Expected Results Banner */}
            <div className={styles.expectedBanner}>
                <div className={styles.bannerTitle}>
                    <TrendingUp size={20} />
                    <span>Expected Results</span>
                </div>
                <div className={styles.bannerStats}>
                    <div className={styles.statItem}>
                        <div className={styles.statValue}>{duration} days</div>
                        <div className={styles.statLabel}>Duration</div>
                    </div>
                    <div className={styles.statItem}>
                        <div className={styles.statValue}>{(sampleSize * 2 / 1000).toFixed(0)}K+</div>
                        <div className={styles.statLabel}>Visitors</div>
                    </div>
                    <div className={styles.statItem}>
                        <div className={styles.statValue}>{confidence}%</div>
                        <div className={styles.statLabel}>Confidence</div>
                    </div>
                    <div className={styles.statItem}>
                        <div className={styles.statValue}>+15%</div>
                        <div className={styles.statLabel}>Target</div>
                    </div>
                </div>
            </div>

            {/* Test Overview */}
            <div className={styles.overviewCard}>
                <div className={styles.overviewHeader}>
                    <div className={styles.overviewTitle}>
                        <div className={styles.statusDot}></div>
                        <h2>{formData.basicInfo?.testName || 'A/B Test'}</h2>
                        <span className={styles.readyBadge}>Ready to Launch</span>
                    </div>
                    <p className={styles.overviewDescription}>
                        {formData.basicInfo?.description || 'Testing different variations to improve performance.'}
                    </p>
                </div>

                <div className={styles.goalSection}>
                    <div className={styles.goalIcon}>
                        <Target size={16} />
                    </div>
                    <span className={styles.goalText}>
                        {formData.basicInfo?.goal || 'Increase conversion rate'}
                    </span>
                </div>
            </div>

            {/* Coupon Code Section - Only for pricing/cart tests */}
            {!isElementTest && (
                <div className={styles.couponSection}>
                    <div className={styles.sectionHeader}>
                        <Tag size={20} />
                        <h3>Discount Code Configuration</h3>
                    </div>

                    <div className={styles.couponCard}>
                        <div className={styles.couponInfo}>
                            <div className={styles.couponLabel}>
                                <span>Discount Code</span>
                                {getDiscountPercentage() > 0 && (
                                    <span className={styles.discountBadge}>{getDiscountPercentage()}% OFF</span>
                                )}
                            </div>

                            <div className={styles.couponDisplay}>
                                {isEditingCoupon ? (
                                    <div className={styles.couponEdit}>
                                        <input
                                            type="text"
                                            value={tempCouponCode}
                                            onChange={(e) => setTempCouponCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                                            className={styles.couponInput}
                                            placeholder="Enter coupon code"
                                            maxLength={20}
                                        />
                                        <div className={styles.couponActions}>
                                            <button
                                                onClick={handleSaveCoupon}
                                                className={styles.saveBtn}
                                                title="Save"
                                            >
                                                <Check size={16} />
                                            </button>
                                            <button
                                                onClick={handleCancelEditCoupon}
                                                className={styles.cancelBtn}
                                                title="Cancel"
                                            >
                                                <X size={16} />
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className={styles.couponView}>
                                        <div className={styles.couponCode}>{couponCode}</div>
                                        <div className={styles.couponActions}>
                                            <button
                                                onClick={handleEditCoupon}
                                                className={styles.editBtn}
                                                title="Edit coupon code"
                                            >
                                                <Edit3 size={16} />
                                            </button>
                                            <button
                                                onClick={handleRegenerateCoupon}
                                                className={styles.regenerateBtn}
                                                title="Generate new code"
                                            >
                                                <RefreshCw size={16} />
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className={styles.couponDescription}>
                                You will need to create this discount code before launching the test.
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Variant Comparison - Dynamic based on test type */}
            <div className={styles.variantSection}>
                <div className={styles.sectionHeader}>
                    <SettingsIcon size={20} />
                    <h3>
                        {isElementTest ? 'Element Position Comparison' :
                            isCartTest ? 'Cart Experience Comparison' : 'Variant Comparison'}
                    </h3>
                </div>

                {isElementTest ? renderElementTestDetails() :
                    isCartTest ? renderCartTestDetails() : renderPricingTestDetails()}
            </div>

            {/* Settings Grid */}
            <div className={styles.settingsGrid}>
                <div className={styles.settingsCard}>
                    <div className={styles.cardHeader}>
                        <SettingsIcon size={20} />
                        <h3>Basic Settings</h3>
                    </div>
                    <div className={styles.settingsContent}>
                        <div className={styles.settingRow}>
                            <span className={styles.settingLabel}>Test Split</span>
                            <span className={styles.settingValue}>{trafficSplit}% / {100 - trafficSplit}%</span>
                        </div>
                        <div className={styles.settingRow}>
                            <span className={styles.settingLabel}>Duration</span>
                            <span className={styles.settingValue}>{duration} days</span>
                        </div>
                        <div className={styles.settingRow}>
                            <span className={styles.settingLabel}>Sample Size</span>
                            <span className={styles.settingValue}>{sampleSize.toLocaleString()} visitors</span>
                        </div>
                        <div className={styles.settingRow}>
                            <span className={styles.settingLabel}>Confidence</span>
                            <span className={styles.settingValue}>{confidence}%</span>
                        </div>
                        <div className={styles.settingRow}>
                            <span className={styles.settingLabel}>Success Metric</span>
                            <span className={styles.settingValue}>{getMetricLabel(basicSettings.metric)}</span>
                        </div>
                    </div>
                </div>

                <div className={styles.settingsCard}>
                    <div className={styles.cardHeader}>
                        <Target size={20} />
                        <h3>Targeting & Management</h3>
                    </div>
                    <div className={styles.settingsContent}>
                        <div className={styles.settingRow}>
                            <span className={styles.settingLabel}>Devices</span>
                            <span className={styles.settingValue}>{getDeviceLabel(advancedSettings.device)}</span>
                        </div>
                        <div className={styles.settingRow}>
                            <span className={styles.settingLabel}>User Type</span>
                            <span className={styles.settingValue}>{getUserTypeLabel(advancedSettings.userType)}</span>
                        </div>
                        <div className={styles.settingRow}>
                            <span className={styles.settingLabel}>Traffic Source</span>
                            <span className={styles.settingValue}>{getTrafficSourceLabel(advancedSettings.trafficSource)}</span>
                        </div>
                        <div className={styles.settingRow}>
                            <span className={styles.settingLabel}>Countries</span>
                            <span className={styles.settingValue}>{getCountryLabel(advancedSettings.countries)}</span>
                        </div>
                        <div className={styles.settingRow}>
                            <span className={styles.settingLabel}>Auto-stop</span>
                            <span className={styles.settingValue}>{getAutoStopLabel(managementSettings.autoStop)}</span>
                        </div>
                        <div className={styles.settingRow}>
                            <span className={styles.settingLabel}>Notifications</span>
                            <span className={styles.settingValue}>{getNotificationsLabel(managementSettings.notifications)}</span>
                        </div>

                        {advancedSettings.referrers && advancedSettings.referrers.length > 0 && (
                            <div className={styles.settingRow} style={{ flexDirection: 'column', alignItems: 'flex-start', borderBottom: 'none' }}>
                                <span className={styles.settingLabel} style={{ marginBottom: '8px' }}>Referrer Sites</span>
                                <div className={styles.tagsList}>
                                    {advancedSettings.referrers.map((referrer, index) => (
                                        <span key={index} className={styles.tag}>{referrer}</span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {advancedSettings.utmParams && advancedSettings.utmParams.length > 0 && (
                            <div className={styles.settingRow} style={{ flexDirection: 'column', alignItems: 'flex-start', borderBottom: 'none', paddingBottom: 0 }}>
                                <span className={styles.settingLabel} style={{ marginBottom: '8px' }}>UTM Parameters</span>
                                <div className={styles.utmList}>
                                    {advancedSettings.utmParams.map((param, index) => (
                                        <div key={index} className={styles.utmParam}>
                                            <code>{param.key}</code> = <span>{param.value}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Additional Settings */}
            <div className={styles.additionalSettings}>
                <div className={styles.sectionHeader}>
                    <Shield size={20} />
                    <h3>Additional Settings</h3>
                </div>

                <div className={styles.additionalGrid}>
                    <div className={styles.qualitySection}>
                        <h4>Quality Assurance</h4>
                        <div className={styles.qualityList}>
                            {qualitySettings.botFiltering && (
                                <div className={styles.qualityItem}>
                                    <CheckCircle2 size={16} />
                                    <span>Bot traffic filtering</span>
                                </div>
                            )}
                            {qualitySettings.duplicateProtection && (
                                <div className={styles.qualityItem}>
                                    <CheckCircle2 size={16} />
                                    <span>Duplicate session protection</span>
                                </div>
                            )}
                            {qualitySettings.segmentOverlap && (
                                <div className={styles.qualityItem}>
                                    <CheckCircle2 size={16} />
                                    <span>Segment overlap detection</span>
                                </div>
                            )}
                            {qualitySettings.dataValidation && (
                                <div className={styles.qualityItem}>
                                    <CheckCircle2 size={16} />
                                    <span>Real-time data validation</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className={styles.targetingSection}>
                        <div className={styles.targetingItem}>
                            <h4>Time Targeting</h4>
                            <p>{advancedSettings.timeTargeting === 'always' ? 'Always Active' :
                                advancedSettings.timeTargeting === 'business' ? 'Business Hours' :
                                    advancedSettings.timeTargeting === 'weekends' ? 'Weekends Only' : 'Custom Schedule'}</p>
                        </div>
                        <div className={styles.targetingItem}>
                            <h4>Browser Targeting</h4>
                            <p>{advancedSettings.browser === 'all' ? 'All Browsers' :
                                advancedSettings.browser === 'chrome' ? 'Chrome Only' :
                                    advancedSettings.browser === 'firefox' ? 'Firefox Only' :
                                        advancedSettings.browser === 'safari' ? 'Safari Only' : 'Edge Only'}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer Navigation */}
            <div className={styles.footer}>
                <button className={styles.backButton} onClick={onBack} disabled={isSubmitting}>
                    <ArrowLeft size={16} />
                    Back to Settings
                </button>
                <div className={styles.actionButtons}>
                    <button
                        className={styles.saveButton}
                        onClick={handleSaveDraft}
                        disabled={isSubmitting}
                    >
                        <Save size={16} />
                        {isSubmitting ? 'Saving...' : 'Save as Draft'}
                    </button>
                    <button
                        className={styles.launchButton}
                        onClick={handleLaunchTest}
                        disabled={isSubmitting}
                    >
                        <Rocket size={16} />
                        {isSubmitting ? 'Launching...' : 'Launch Test'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TestSummary;